import { useEffect, useId, useRef } from 'react';

/**
 * Cloudflare Turnstile widget for the sign-up forms.
 *
 * Renders nothing when no site key is configured, which is what makes Turnstile opt-in per
 * environment: the backend's `verifyTurnstile` is a no-op without `TURNSTILE_SECRET_KEY`, so a
 * form that never mounts a widget still submits successfully. Both halves have to be configured
 * before anything is enforced.
 *
 * The token is single-use and expires after roughly five minutes, so the widget refreshes itself
 * (`onExpire`) rather than leaving a stale token on a form somebody left open.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

/**
 * The action the sign-up widgets declare. Must equal `SIGNUP_ACTION` in
 * `backend/src/auth/turnstile.ts`, which rejects any token whose siteverify `action`
 * differs — that is what stops a token minted on another protected form opening sign-up.
 */
export const TURNSTILE_SIGNUP_ACTION = 'signup';

/**
 * The Agentic Flow Studios Turnstile site key, shared by every portal and the marketing site.
 *
 * A site key is public by design — it ships to the browser in the rendered widget — so this is a
 * constant rather than per-app configuration. It lives here because the alternative (a
 * `VITE_TURNSTILE_SITE_KEY` in each portal's `.env`) silently failed for half the portals:
 * `.gitignore` ignores `*.env*`, and only clinic-portal's and skill-viper-portal's env files are
 * force-tracked, so job-plan and real-estate-studio would have built with no key, rendered no
 * widget, and had sign-up refused by a backend that requires a token.
 *
 * One widget covers every environment (its hostname list includes localhost), which is why one key
 * is correct here. `VITE_TURNSTILE_SITE_KEY` still overrides it if an environment ever needs its
 * own widget. The *secret* half is never here — it is a Worker secret, per environment.
 */
export const TURNSTILE_SITE_KEY = '0x4AAAAAAEikV6X_PFFdCfqt';

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
      tabindex?: number;
    }
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Loads the Turnstile script once per document, reusing the in-flight promise for later mounts. */
let scriptPromise: Promise<void> | null = null;

/**
 * The currently mounted widget's id. Module-scoped because the sign-up forms render exactly one
 * widget at a time and `resetTurnstile` is called from a submit handler that has no ref to it.
 */
let activeWidgetId: string | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile script failed'));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export type TurnstileWidgetProps = {
  /** Public site key. Undefined or empty disables the widget entirely. */
  siteKey?: string;
  /**
   * The surface this widget protects. The backend requires siteverify's `action` to equal this
   * exactly, so a token earned on one form cannot be replayed at another — keep it in step with
   * `SIGNUP_ACTION` in `backend/src/auth/turnstile.ts`. Cloudflare's limit is 1-32 characters of
   * alphanumerics, underscore, and hyphen.
   */
  action: string;
  /** Receives the token to submit, or null once it expires or errors. */
  onToken: (token: string | null) => void;
  className?: string;
};

export function TurnstileWidget({ siteKey, action, onToken, className }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const instanceId = useId();

  // Kept in a ref so a re-created callback prop never tears down and re-renders the widget, which
  // would invalidate a token the user has already earned.
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey) return;

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        const widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: 'auto',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
        widgetIdRef.current = widgetId;
        activeWidgetId = widgetId;
      })
      .catch(() => {
        // A blocked or unreachable script must not strand the form. The backend fails open on an
        // unreachable siteverify for the same reason, so sign-up still completes.
        onTokenRef.current(null);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        if (activeWidgetId === widgetIdRef.current) activeWidgetId = null;
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, action]);

  if (!siteKey) return null;

  return <div ref={containerRef} id={`turnstile-${instanceId}`} className={className} />;
}

/**
 * Discards a spent token and issues a fresh challenge.
 *
 * A Turnstile token is single-use: once the backend has redeemed it, resubmitting the same form
 * fails verification even though the user did nothing wrong. A form that stays on screen after a
 * rejected submit must reset before the user can retry.
 */
export function resetTurnstile() {
  const api = window.turnstile;
  if (api && activeWidgetId) api.reset(activeWidgetId);
}
